import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService, PlatformCustomerGroupRecord } from '../finance/finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-platform-customer-groups',
  imports: [AinPageComponent, AinSearchAccordionComponent, AinStateComponent, FormsModule, TableModule, ButtonModule],
  template: `
    <ain-page [eyebrow]="t('platform.eyebrow')" [title]="t('platform.customerGroups.title')" [description]="t('platform.customerGroups.description')">
      <div page-actions>
        <button pButton type="button" [label]="t('common.create')" (click)="openCreate()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <ain-search-accordion [title]="t('platform.search.title')">
          <section class="search-grid">
            <input [(ngModel)]="filters.query" [placeholder]="t('platform.customerGroups.searchPlaceholder')" />
            <select [(ngModel)]="filters.groupType">
              <option value="">{{ t('common.all') }}</option>
              @for (option of groupTypeOptions; track option.value) {
                <option [value]="option.value">{{ t(option.labelKey) }}</option>
              }
            </select>
            <button pButton type="button" [label]="t('platform.search.apply')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
          </section>
        </ain-search-accordion>

        @if (!rows().length) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
        } @else {
          <p-table [value]="rows()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('platform.customerGroups.fields.groupCode') }}</th>
                <th>{{ t('platform.customerGroups.fields.name') }}</th>
                <th>{{ t('platform.customerGroups.fields.groupType') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('platform.customerGroups.fields.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.groupCode }}</td>
                <td>{{ row.name }}</td>
                <td>{{ groupTypeLabel(row.groupType) }}</td>
                <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
                <td>
                  <button pButton type="button" size="small" [label]="t('common.edit')" text="true" (click)="openDetail(row.id)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      }

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .search-grid { display: grid; grid-template-columns: 2fr 1fr auto auto; gap: 8px; align-items: center; }
    .search-grid input, .search-grid select { height: 38px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; width: 100%; }
    .message.error { margin-top: 10px; color: #b91c1c; }
    @media (max-width: 1200px) { .search-grid { grid-template-columns: 1fr; } }
  `]
})
export class PlatformCustomerGroupsComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly rows = signal<PlatformCustomerGroupRecord[]>([]);
  readonly errorMessage = signal('');
  readonly canLoad = computed(() => !this.loading());

  readonly groupTypeOptions = [
    { value: 0, labelKey: 'platform.customerGroups.type.pricing' },
    { value: 1, labelKey: 'platform.customerGroups.type.credit' },
    { value: 2, labelKey: 'platform.customerGroups.type.quota' },
    { value: 3, labelKey: 'platform.customerGroups.type.accountHierarchy' },
    { value: 4, labelKey: 'platform.customerGroups.type.loyaltySegment' },
    { value: 5, labelKey: 'platform.customerGroups.type.campaignSegment' },
    { value: 6, labelKey: 'platform.customerGroups.type.complianceWatchlist' },
    { value: 7, labelKey: 'platform.customerGroups.type.other' }
  ];

  filters = { query: '', groupType: '' };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    if (!this.canLoad()) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    this.api.listPlatformCustomerGroups({
      query: this.filters.query,
      groupType: this.filters.groupType,
      skip: 0,
      take: 200
    }).subscribe({
      next: response => {
        this.rows.set(response.items);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  clearFilters(): void {
    this.filters.query = '';
    this.filters.groupType = '';
    this.load();
  }

  openCreate(): void {
    void this.router.navigate(['/platform/customer-groups/new']);
  }

  openDetail(groupId: string): void {
    void this.router.navigate(['/platform/customer-groups', groupId]);
  }

  groupTypeLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const option = this.groupTypeOptions.find(item => item.value === numeric);
    return option ? this.t(option.labelKey) : `${value}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
